import { create } from 'zustand';

/** Full tenant info available after login / /auth/me */
export interface AvailableTenant {
  id: string;
  name: string;
  slug: string;
  roleCode: string;
  type: 'ORGANIZATION' | 'SUBSIDIARY';
  isSystem: boolean;
  depth: number;
  parentId: string | null;
  rootTenantId: string;
  organizationName: string | null;
  accessType: string;
}

/** Authenticated user profile — hydrated from GET /auth/me */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  activeTenantId: string;
  accessibleTenants: AvailableTenant[];
  roleCode: string;
  roles: string[];
  permissions: string[];
  isSuperAdmin: boolean;
  avatarUrl?: string | undefined;
  idpProvider: 'google' | 'microsoft';
  /** Per-user feature flags from /auth/me. Empty map when the backend omits the field. */
  featureFlags: Record<string, boolean>;
  /** Generation token for the flags map — used by clients that need to invalidate caches. */
  flagsGeneration?: string;
}

// ---------------------------------------------------------------------------
// Token persistence helpers (scoped: localStorage = shared login,
// sessionStorage = per-tab subsidiary override)
// ---------------------------------------------------------------------------
const TOKEN_KEYS = { access: 'auth_access_token', refresh: 'auth_refresh_token' } as const;

/** Where the active token lives: shared login (`local`) or per-tab override (`session`). */
export type TokenScope = 'local' | 'session';

function storageFor(scope: TokenScope): Storage {
  return scope === 'session' ? sessionStorage : localStorage;
}

/** Boot scope: a per-tab (sessionStorage) token wins over the shared login token. */
function detectInitialScope(): TokenScope {
  try {
    return sessionStorage.getItem(TOKEN_KEYS.access) ? 'session' : 'local';
  } catch {
    return 'local';
  }
}

// Module-level active scope — re-evaluated on each module load (per tab).
let tokenScope: TokenScope = detectInitialScope();

/** Reads a token session-first, then localStorage, so a per-tab override wins. */
function loadToken(key: string): string | null {
  try {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes both tokens to the store named by `scope`. */
function persistTokens(accessToken: string, refreshToken: string, scope: TokenScope): void {
  try {
    const store = storageFor(scope);
    store.setItem(TOKEN_KEYS.access, accessToken);
    store.setItem(TOKEN_KEYS.refresh, refreshToken);
  } catch {
    // storage unavailable (e.g. private browsing quota exceeded) — tokens stay in memory only
  }
}

/** Reads a token directly from the SHARED (localStorage) store, ignoring any
 *  per-tab sessionStorage override. Used by impersonation, which is always a
 *  shared-scope operation and must capture/restore the real shared login —
 *  not whatever scope happens to be active in the current tab. */
function loadSharedToken(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

/** Clears tokens from BOTH stores so no per-tab override survives a sign-out. */
function removePersistedTokens(): void {
  try {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
    sessionStorage.removeItem(TOKEN_KEYS.access);
    sessionStorage.removeItem(TOKEN_KEYS.refresh);
  } catch {
    // Ignore
  }
}

// ---------------------------------------------------------------------------
// Login-method persistence helpers (localStorage)
// ---------------------------------------------------------------------------
/** How the current session was initiated. Temporary client-side signal until
 *  backend returns `authMethod` on `/auth/me`. See `docs/auth-login-method.md`. */
export type LoginMethod = 'sso' | 'password';

const LOGIN_METHOD_KEY = 'auth_login_method';

/** Reads the persisted login method. Returns `null` for pre-existing sessions
 *  predating this signal, or when localStorage is unavailable. */
function loadLoginMethod(): LoginMethod | null {
  try {
    const value = localStorage.getItem(LOGIN_METHOD_KEY);
    return value === 'sso' || value === 'password' ? value : null;
  } catch {
    return null;
  }
}

/** Persists the login method alongside the tokens so it survives tab close. */
function persistLoginMethod(method: LoginMethod): void {
  try {
    localStorage.setItem(LOGIN_METHOD_KEY, method);
  } catch {
    // localStorage unavailable — value stays in memory only
  }
}

function removePersistedLoginMethod(): void {
  try {
    localStorage.removeItem(LOGIN_METHOD_KEY);
  } catch {
    // Ignore
  }
}

// ---------------------------------------------------------------------------
// Impersonation persistence helpers (sessionStorage — survives page refresh)
// ---------------------------------------------------------------------------
const IMPERSONATION_KEY = 'impersonation_context';

interface ImpersonationContext {
  originalAccessToken: string;
  originalRefreshToken: string;
  originalUser: AuthUser | null;
  impersonatorSessionId: string;
  targetUser: { id: string; email: string; firstName: string; lastName: string };
  reason: string;
  startedAt: string;
}

function loadImpersonationContext(): ImpersonationContext | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationContext) : null;
  } catch {
    return null;
  }
}

function persistImpersonationContext(ctx: ImpersonationContext): void {
  try {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(ctx));
  } catch {
    // sessionStorage unavailable
  }
}

function removeImpersonationContext(): void {
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    // Ignore
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  isSwitchingTenant: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  /** How the current session was initiated. `null` for sessions predating
   *  this signal, or until the user re-logs in. See `docs/auth-login-method.md`. */
  loginMethod: LoginMethod | null;
  /** Impersonation state */
  isImpersonating: boolean;
  impersonationContext: ImpersonationContext | null;
}

interface AuthActions {
  setUser: (user: AuthUser) => void;
  clearUser: () => void;
  setTokens: (accessToken: string, refreshToken: string, scope?: TokenScope) => void;
  clearTokens: () => void;
  setActiveTenantId: (tenantId: string) => void;
  setIsInitializing: (value: boolean) => void;
  setIsSwitchingTenant: (value: boolean) => void;
  /** Records how this session was initiated. Called from the credentials and
   *  OAuth-exchange paths. Persisted to localStorage so it survives tab close. */
  setLoginMethod: (method: LoginMethod) => void;
  /** Replace the resolved feature-flag map. Called when /feature-flags/me refetches
   *  after an admin toggle so the sidebar (and other useFeatureFlag consumers) update live. */
  setFeatureFlags: (flags: Record<string, boolean>, generation?: string) => void;
  /** Start impersonating a user — saves current session and swaps tokens */
  startImpersonation: (response: {
    accessToken: string;
    refreshToken: string;
    impersonatorSessionId: string;
    targetUser: { id: string; email: string; firstName: string; lastName: string };
    reason: string;
  }) => void;
  /** Stop impersonating — restores original admin session */
  stopImpersonation: () => void;
}

/**
 * Auth store — user profile is NOT persisted (re-hydrated from GET /auth/me).
 * Tokens are persisted in localStorage so sessions survive tab close.
 */
export const useAuthStore = create<AuthState & AuthActions>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isInitializing: true,
  isSwitchingTenant: false,
  accessToken: loadToken(TOKEN_KEYS.access),
  refreshToken: loadToken(TOKEN_KEYS.refresh),
  loginMethod: loadLoginMethod(),
  isImpersonating: loadImpersonationContext() !== null,
  impersonationContext: loadImpersonationContext(),

  setUser: (user) => {
    set({ user, isAuthenticated: true, isInitializing: false });
  },

  clearUser: () => {
    removePersistedTokens();
    removePersistedLoginMethod();
    tokenScope = 'local';
    set({
      user: null,
      isAuthenticated: false,
      isInitializing: false,
      isSwitchingTenant: false,
      accessToken: null,
      refreshToken: null,
      loginMethod: null,
    });
  },

  // Persist tokens to the given scope (login → 'local', subsidiary switch →
  // 'session'); omit scope to keep the current scope (used by token refresh).
  setTokens: (accessToken, refreshToken, scope) => {
    const nextScope = scope ?? tokenScope;
    tokenScope = nextScope;
    persistTokens(accessToken, refreshToken, nextScope);
    set({ accessToken, refreshToken });
  },

  // Clear tokens from both stores and reset to the shared (local) scope.
  clearTokens: () => {
    removePersistedTokens();
    tokenScope = 'local';
    set({ accessToken: null, refreshToken: null });
  },

  setActiveTenantId: (tenantId) => {
    set((state) => {
      if (!state.user) return state;
      return { user: { ...state.user, activeTenantId: tenantId } };
    });
  },

  setIsInitializing: (value) => {
    set({ isInitializing: value });
  },

  setIsSwitchingTenant: (value) => {
    set({ isSwitchingTenant: value });
  },

  setLoginMethod: (method) => {
    persistLoginMethod(method);
    set({ loginMethod: method });
  },

  setFeatureFlags: (flags, generation) => {
    set((state) => {
      if (!state.user) return state;
      const nextUser: AuthUser = { ...state.user, featureFlags: flags };
      if (generation !== undefined) nextUser.flagsGeneration = generation;
      return { user: nextUser };
    });
  },

  startImpersonation: (response) => {
    const { user } = get();
    // Impersonation is a shared-scope ('local') operation, so the "original"
    // to restore on stop MUST be the shared localStorage login — not
    // get().accessToken, which on a tab that already switched subsidiary is
    // the per-tab session-scoped token. Reading the store here would leak
    // that session token into localStorage on stopImpersonation, corrupting
    // the shared cross-tab login.
    const ctx: ImpersonationContext = {
      originalAccessToken: loadSharedToken(TOKEN_KEYS.access),
      originalRefreshToken: loadSharedToken(TOKEN_KEYS.refresh),
      originalUser: user,
      impersonatorSessionId: response.impersonatorSessionId,
      targetUser: response.targetUser,
      reason: response.reason,
      startedAt: new Date().toISOString(),
    };
    persistImpersonationContext(ctx);
    // Impersonation operates at the shared (local) scope. Drop any per-tab
    // subsidiary override so it can't shadow the impersonation token, then pin
    // 'local' so refreshes stay put.
    try {
      sessionStorage.removeItem(TOKEN_KEYS.access);
      sessionStorage.removeItem(TOKEN_KEYS.refresh);
    } catch {
      // storage unavailable — nothing to clear
    }
    tokenScope = 'local';
    persistTokens(response.accessToken, response.refreshToken, 'local');
    set({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      isImpersonating: true,
      impersonationContext: ctx,
    });
  },

  stopImpersonation: () => {
    const { impersonationContext } = get();
    if (!impersonationContext) return;
    // Restore original tokens
    persistTokens(
      impersonationContext.originalAccessToken,
      impersonationContext.originalRefreshToken,
      'local',
    );
    removeImpersonationContext();
    set({
      accessToken: impersonationContext.originalAccessToken,
      refreshToken: impersonationContext.originalRefreshToken,
      isImpersonating: false,
      impersonationContext: null,
    });
  },
}));
